/**
 * Inline display + upload of sample documents attached to a template.
 * Shows each sample as an expandable card with the extracted content inline.
 */
import { useState } from 'react'
import { Upload, Trash2, ChevronDown, ChevronRight, File as FileIcon } from 'lucide-react'
import { deleteSample, uploadSample } from './api'
import type { Sample } from './types'

interface Props {
  templateId: string
  samples: Sample[]
  readOnly?: boolean
  onChanged: () => Promise<void>
}

export default function SamplesPanel({ templateId, samples, readOnly, onChanged }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [showUploader, setShowUploader] = useState(false)

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleUpload = async () => {
    if (!title.trim()) {
      setError('Title is required')
      return
    }
    setUploading(true)
    setError(null)
    try {
      await uploadSample({
        template: templateId,
        title: title.trim(),
        notes: notes.trim() || undefined,
        file,
      })
      setTitle('')
      setNotes('')
      setFile(null)
      setShowUploader(false)
      await onChanged()
    } catch (err: any) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Delete sample "${title}"?`)) return
    try {
      await deleteSample(id)
      await onChanged()
    } catch (err: any) {
      setError(err.message || 'Delete failed')
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Samples</h3>
        {!readOnly && !showUploader && (
          <button
            type="button"
            onClick={() => setShowUploader(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md"
          >
            <Upload className="w-3.5 h-3.5" />
            Add sample
          </button>
        )}
      </div>

      {showUploader && !readOnly && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Title</span>
            <input
              className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Navy UAS CNS — 2023"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">File</span>
            <input
              type="file"
              accept=".pdf,.docx,.txt,.md"
              className="mt-1 w-full text-sm"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <span className="text-xs text-muted-foreground mt-1 block">
              Optional. If not provided, a text-only sample will be created from the Notes field.
            </span>
          </label>
          <label className="block">
            <span className="text-sm font-medium">Notes</span>
            <textarea
              className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What makes this a good or bad example?"
            />
          </label>
          {error && <div className="text-sm text-destructive">{error}</div>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowUploader(false); setError(null) }}
              className="px-3 py-1.5 text-sm border border-border rounded-md"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading}
              className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      )}

      {samples.length === 0 && !showUploader && (
        <div className="text-sm text-muted-foreground italic">
          No samples yet. {readOnly ? '' : 'Add one to help the writing agent understand what good output looks like.'}
        </div>
      )}

      {samples.map((s) => {
        const isOpen = expanded.has(s.id)
        return (
          <div key={s.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => toggle(s.id)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={isOpen ? 'Collapse' : 'Expand'}
              >
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              <FileIcon className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground truncate">{s.title}</div>
                <div className="text-xs text-muted-foreground">
                  {s.uploaded_by_name || 'Unknown'} · {new Date(s.uploaded_at).toLocaleDateString()}
                  {s.file_size > 0 && ` · ${Math.round(s.file_size / 1024)} KB`}
                </div>
              </div>
              {s.file_url && (
                <a
                  href={s.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  Download
                </a>
              )}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleDelete(s.id, s.title)}
                  className="p-1.5 text-destructive hover:bg-destructive/10 rounded"
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {isOpen && (
              <div className="border-t border-border px-4 py-3 bg-muted/20 space-y-2">
                {s.notes && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">Notes</div>
                    <div className="text-sm text-foreground whitespace-pre-wrap">{s.notes}</div>
                  </div>
                )}
                {s.content && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">Content</div>
                    <pre className="text-xs text-foreground whitespace-pre-wrap font-sans max-h-96 overflow-auto">
                      {s.content}
                    </pre>
                  </div>
                )}
                {!s.notes && !s.content && (
                  <div className="text-sm text-muted-foreground italic">
                    No extracted content. {s.file_url ? 'Download the file to view.' : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
